import type { ReactNode } from "react";

interface PageHeaderProps {
  /** The page's single H1 text. Kept verbatim so SEO is unaffected. */
  title: string;
  /** Short uppercase label above the title, rendered with the eyebrow utility. */
  eyebrow?: string;
  /** One- or two-sentence summary shown in the `lead` style. */
  description?: ReactNode;
  /** Optional chips / stamps rendered under the description. */
  meta?: ReactNode;
  /** Width is controlled by the caller so existing page layout is unchanged. */
  className?: string;
}

/**
 * PageHeader is the shared editorial header for content and listing pages.
 *
 * It deliberately renders no <section> and imposes no max-width: callers keep
 * their existing wrapper elements, so page structure and DOM order (and
 * therefore the document outline) are unchanged. Only the typography is
 * unified — Merriweather display title, `lead` description, `eyebrow` label.
 */
export function PageHeader({ title, eyebrow, description, meta, className }: PageHeaderProps) {
  return (
    <div className={className}>
      {eyebrow ? <p className="eyebrow">{eyebrow}</p> : null}
      <h1 className="mt-3 font-[family-name:var(--font-display)] text-4xl font-semibold leading-[1.05] tracking-[-0.02em] text-[var(--color-text)] text-balance sm:text-5xl">
        {title}
      </h1>
      {description ? <p className="lead mt-4 max-w-2xl">{description}</p> : null}
      {meta ? <div className="mt-5 flex flex-wrap items-center gap-2.5">{meta}</div> : null}
    </div>
  );
}